import React, {useEffect, useRef} from 'react';
import {Button, Form, Row} from 'react-bootstrap';
import {
    capacityUnitOptions,
    loadCellClassOptions,
    loadCellModeOptions,
    loadCellOutputOptions,
    vfdParityOptions
} from 'constants/apiConstants';
import HiddenInput from 'components/Form/HiddenInput';
import Input from 'components/Form/Input';
import Select from 'components/Form/Select';
import Switch from 'components/Form/Switch';
import {formatCraneWeightFormula} from 'utils/Utils';

const DEFAULT_MOTION_OPTION = {label: '-- Select motion --', value: ''};

export default function LoadCellForm({
    values, dirty, isValid, isSubmitting, handleSubmit, motions, isEditable, isLoading, onCloseButtonClick, errors
}) {
    const isMounted= useRef(false);
    const isDisabled = !isEditable || isLoading;

    useEffect(() => {
        isMounted.current = true;
        return () => isMounted.current = false;
    }, []);

    const motionOptions = [
        DEFAULT_MOTION_OPTION,
        ...Object.values(motions).map(motion => ({label: motion.motion_name, value: motion.id}))
    ];
    const mode = Number(values.mode) || NaN;
    const formula = !isNaN(mode) ? (mode === 2 ? 'ax^2+bx+c' : 'ax+b') : '—';
    const weight = formatCraneWeightFormula(mode, values.calibration_a, values.calibration_b, values.calibration_c) || '—';
    const addressLabel = values.crane_communication_type === 'PLC' ? 'Address (e.g. 192.168.1.100)' : 'Address (e.g. 0x1F)';
    const showAnalogLoadcellSettings = Number(values.output) === 1;
    const showDigitalLoadcellSettings = Number(values.output) === 2;
    const showModbusRTUSettings = values.crane_communication_type === 'MODBUSRTU';
    const showCalibrationParams = showAnalogLoadcellSettings && !isNaN(mode);
    const showCalibrationC = showCalibrationParams && mode === 2;

    return (
        <Form onSubmit={handleSubmit}>
            <Row>
                <Input label="Name" type="text" name="name" required disabled={isDisabled} />
                <Select label="Motion" name="motion_id" options={motionOptions} required disabled={isDisabled} />
            </Row>
            <Row>
                <Input label="Manufacturer" type="text" name="manufacturer" disabled={isDisabled} />
                <Select label="Class" name="lc_class" options={loadCellClassOptions} disabled={isDisabled} />
            </Row>
            <Row>
                <Input label="Part number" type="text" name="part_no" disabled={isDisabled} />
                <Input label="Serial number" type="text" name="serial_no" disabled={isDisabled} />
            </Row>
            <Row>
                <Select label="Unit" name="unit" options={capacityUnitOptions} required disabled={isDisabled} />
                <Select label="Output" name="output" options={loadCellOutputOptions} required disabled={isDisabled} />
            </Row>
            {showDigitalLoadcellSettings &&
                <>
                    <Row>
                        <Input label="Crane communication type" type="text" name="crane_communication_type" disabled />
                        <Input label={addressLabel} type="text" name="mb_address" required disabled={isDisabled} />
                    </Row>
                    {showModbusRTUSettings &&
                        <Row>
                            <Input label="Baud rate (e.g. 9600)" type="number" min="300" step="100" name="mb_baud_rate" disabled={isDisabled} />
                            <Select label="Parity" name="mb_parity" options={vfdParityOptions} disabled={isDisabled} />
                        </Row>}
                </>}
            {showAnalogLoadcellSettings &&
                <>
                    <Row>
                        <Input label="Register (e.g. 79)" type="number" min="0" step="1" name="modbus_register" required disabled={isDisabled} />
                        <Select label="Mode" name="mode" options={loadCellModeOptions} disabled={isDisabled} />
                    </Row>
                    <Row>
                        <Input label="Calibration formula" type="text" name="formula" value={formula} disabled />
                        <Input label="Load cell output conversion equation" type="text" name="weight" value={weight} disabled />
                    </Row>
                    {showCalibrationParams &&
                        <Row>
                            <>
                                <Input label="a" type="number" name="calibration_a" disabled={isDisabled} />
                                <Input label="b" type="number" name="calibration_b" disabled={isDisabled} />
                                {showCalibrationC && <Input label="c" type="number" name="calibration_c" disabled={isDisabled} />}
                            </>
                        </Row>}
                </>}
            <Row>
                <Switch label="Active" id="active" name="active" value="1" disabled={isDisabled} />
            </Row>
            <HiddenInput name="id" />
            <HiddenInput name="ts" />
            {isEditable && <Button type="submit" className="btn-form-submit" disabled={!dirty || !isValid || isSubmitting || isDisabled}>Save</Button>}
            <Button type="button" className="btn-form-close" onClick={onCloseButtonClick} disabled={isSubmitting}>Close</Button>
        </Form>
    )
};
